import { nanoid } from 'nanoid';
import { FilterOperator, type TFilterValue } from '../common';

export interface FilterRuleProps {
  id?: string;
  columnId: string;
  operator?: FilterOperator;
  value?: TFilterValue;
}

export default class FilterRule {
  id: string;
  columnId: string;
  operator: FilterOperator;
  value?: TFilterValue;
  type: 'rule' = 'rule';

  constructor({ id = nanoid(), columnId, operator = FilterOperator.Includes, value = '' }: FilterRuleProps) {
    this.id = id;
    this.columnId = columnId;
    this.operator = operator;
    this.value = value;
  }

  setColumnId(columnId: string) {
    this.columnId = columnId;
  }

  setOperator(operator: FilterOperator) {
    this.operator = operator;
  }

  setValue(value: TFilterValue) {
    this.value = value;
  }

  getString(s?: TFilterValue): string {
    return s?.toString()?.trim()?.toLowerCase() || '';
  }

  isDate(d: Date): d is Date {
    return !Number.isNaN(d?.getTime());
  }

  cellIncludes(c?: string): boolean {
    if (!this.value) return true;
    return this.getString(c)?.includes(this.getString(this.value));
  }

  cellDoesNotInclude(c?: string): boolean {
    if (!this.value) return true;
    return !this.getString(c)?.includes(this.getString(this.value));
  }

  cellIsEqualTo(c?: string): boolean {
    if (!this.value) return true;
    return this.getString(c) === this.getString(this.value);
  }

  cellIsNotEqualTo(c?: string): boolean {
    if (!this.value) return true;
    return this.getString(c) !== this.getString(this.value);
  }

  cellIsEmpty(c?: string): boolean {
    return !c;
  }

  cellIsNotEmpty(c?: string): boolean {
    return !!c;
  }

  cellIsBeforeDate(c?: string): boolean {
    if (!this.value) return true;

    if (c) {
      const cell = new Date(c);
      const value = new Date(this.value.toString());

      if (this.isDate(cell) && this.isDate(value)) {
        return cell.getTime() < value.getTime();
      }
    }

    return false;
  }

  cellIsAfterDate(c?: string): boolean {
    if (!this.value) return true;

    if (c) {
      const cell = new Date(c);
      const value = new Date(this.value.toString());

      if (this.isDate(cell) && this.isDate(value)) {
        return cell.getTime() > value.getTime();
      }
    }

    return false;
  }

  cellIsTrue(c?: TFilterValue) {
    return Boolean(c);
  }

  cellIsFalse(c?: TFilterValue) {
    return !c;
  }

  meetsCriteria(cellValue?: TFilterValue): boolean {
    const c = cellValue?.toString();

    switch (this.operator) {
      // case 1 - cell value includes search value
      case FilterOperator.Includes:
        return this.cellIncludes(c);
      // case 2 - cell value does NOT include search value
      case FilterOperator.DoesNotInclude:
        return this.cellDoesNotInclude(c);
      // case 3 - cell value is EQUAL to the search value
      case FilterOperator.IsEqualTo:
        return this.cellIsEqualTo(c);
      // case 4 - cell value is NOT equal to the search value
      case FilterOperator.IsNotEqualTo:
        return this.cellIsNotEqualTo(c);
      // case 5 - cell is empty (no value)
      case FilterOperator.IsEmpty:
        return this.cellIsEmpty(c);
      // case 6 - cell is NOT empty (has any type of value)
      case FilterOperator.IsNotEmpty:
        return this.cellIsNotEmpty(c);
      // case 7 - cell is a date and is BEFORE the searched date
      case FilterOperator.IsBefore:
        return this.cellIsBeforeDate(c);
      // case 8 - cell is a date and is AFTER the searched date
      case FilterOperator.IsAfter:
        return this.cellIsAfterDate(c);
      // case 9 - cell value is "true"
      case FilterOperator.IsTrue:
        return this.cellIsTrue(cellValue);
      // case 10 - cell value is NOT "true" (false)
      case FilterOperator.IsFalse:
        return this.cellIsFalse(cellValue);
      // default to false (this shouldn't ever happen)
      default:
        return false;
    }
  }

  getSearchParamValue() {
    const columnId = this.columnId;
    const operator = this.operator;

    const value = this.value;

    switch (operator) {
      case FilterOperator.Includes:
        return { property: columnId, operator: 'includes', value };
      case FilterOperator.DoesNotInclude:
        return { property: columnId, operator: 'notIncludes', value };
      case FilterOperator.IsEqualTo:
        return { property: columnId, operator: 'eq', value };
      case FilterOperator.IsNotEqualTo:
        return { property: columnId, operator: 'neq', value };
      case FilterOperator.IsEmpty:
        return { property: columnId, operator: 'isEmpty' };
      case FilterOperator.IsNotEmpty:
        return { property: columnId, operator: 'isNotEmpty' };
      case FilterOperator.IsTrue:
        return { property: columnId, operator: 'eq', value: true };
      case FilterOperator.IsFalse:
        return { property: columnId, operator: 'neq', value: true };
      case FilterOperator.IsAfter:
        return {
          property: columnId,
          operator: 'gte',
          value: new Date(value?.toString() ?? ''),
        };
      case FilterOperator.IsBefore:
        return {
          property: columnId,
          operator: 'lte',
          value: new Date(value?.toString() ?? ''),
        };
      case FilterOperator.IsNull:
        return {
          property: columnId,
          operator: 'isNull',
        };
      default:
        return [];
    }
  }
}
